import { BaseEvent, EventStream } from '../api/events'
import { ResourceMap } from './manifest'
import { ResourceObservable } from './support/ResourceObservable'
import { Entity, ResourceType } from './types'
import { logger } from '../utils/logging'

/**
 * Any storage engine can be used here, but it should be optimized for speed of individual
 * get/set operations.
 *
 * @note this used to be zustand but that wasn't adding any value here - David
 */
const REGISTRY = new Map<string, ResourceObservable<any>>()

/**
 * Store a valid resource in the registry.
 *
 * @param resource
 */
export function registerResource<T extends ResourceType>(resource: Entity<T>) {
  const existingResource = REGISTRY.get(resource.id)
  if (existingResource) {
    existingResource.next(resource)
  } else {
    const observable = new ResourceObservable(resource)
    REGISTRY.set(resource.id, observable)
  }
}

/**
 * Given a list of resources, usually from a response like a list or something, cache the resources
 * so they'll stay synced
 *
 * @param resources
 */
export function registerMultipleResources<T extends ResourceType>(resources: Array<Entity<T>>): string[] {
  const resourceIds: string[] = []
  resources.forEach((resource) => {
    registerResource(resource)
    resourceIds.push(resource.id)
  })
  return resourceIds
}

/**
 * Returns an RXJS observable for the specified resource type and id. Returns null if the resource is
 * not found in the registry.
 */
export function getResourceObservable<T extends ResourceType>(id: string): ResourceObservable<Entity<T>> | null {
  return REGISTRY.get(id) || null
}

/**
 * Given an id and a latest timestamp, determine if the data in the cache is outdated, and if so, refresh
 *
 * @todo debounce reloading - handle a rapid series of events properly
 *
 * @param id
 * @param latestUpdatedAtStr
 */
async function reloadResourceIfStale(id: string, latestUpdatedAtStr: string) {
  const latestUpdatedAt = new Date(latestUpdatedAtStr)
  const resource = REGISTRY.get(id)
  if (resource) {
    const current = resource.getValue()
    const cacheDate = current.updatedAt ? new Date(current.updatedAt) : null
    if (cacheDate && latestUpdatedAt <= cacheDate) {
      return
    }
    const fetcher = resourceFetcherFromId(id)
    if (fetcher) {
      const res = await fetcher()
      if (res) {
        resource.next(res)
        return res
      }
    }
  }
}

/**
 * Trigger events on any known entities
 *
 * @param event
 */
function handleResourceEvent<E extends BaseEvent>(event: E) {
  try {
    const [resource, topic] = event.topic.split(':')
    const targetId = getTargetIdFromEvent(resource, event)
    if (targetId) {
      try {
        const target = REGISTRY.get(targetId)
        if ((target && topic === 'updated') || topic === 'archived' || topic === 'unarchived' || topic === 'deleted') {
          reloadResourceIfStale(targetId, (event as any).attributes?.targetUpdatedAt ?? (event as any).createdAt).then(
            (res) => {
              console.log('updated resource in cache', targetId, topic, res)
            }
          )
        }
      } catch (e) {
        logger.error('error thrown in storage.ts running reloadResourceIfStale', { error: e })
      }
    }
  } catch (e: any) {
    logger.error(`error in storage.ts running getTargetIdFromEvent`, {
      error: e,
      event,
    })
  }
}

export function getTargetIdFromEvent(entity: string, event: any): string | null {
  //this is hacky
  if (entity === 'sheet') return event.context.workbookId
  return event.target || event.origin?.id || event.context?.[`${entity}Id`] || null
}

/**
 * Helper utility for getting the resource from the ID
 * @param id
 */
export function resourceFetcherFromId(id: string): void | (() => Promise<any>) {
  const shorthand = id.split('_')[1]

  const handler = Object.values(ResourceMap).find((v) => v.shorthand === shorthand)

  if (handler && 'get' in handler) {
    return async () => {
      const res = await handler.get(id)
      return res.data
    }
  }
}

/**
 * Delete a valid resource from the registry.
 *
 * @param resource
 */
export function unregisterResource<T extends ResourceType>(id: Entity<T>) {
  const existingResource = REGISTRY.get(id)
  if (existingResource) {
    REGISTRY.delete(id)
    reloadResourceIfStale(id, new Date().toISOString())
  }
}

EventStream.subscribe(handleResourceEvent)
